import React, { useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setChannel } from '../features/channelSlice';
import AgentView from './AgentView';

export default function HomeView({ onAppOpen }) {
  const dispatch = useDispatch();
  const channelConfig = useSelector((state) => state.channel.channelConfig);
  const homeBackgroundImage = useSelector((state) => state.channel.homeBackgroundImage);
  const calendarDate = useSelector((state) => state.channel.calendarDate);
  const unreadCounts = useSelector((state) => state.channel.unreadCounts || {});
  const isCallInProgress = useSelector((state) => state.channel.isCallInProgress);
  const tileRefs = useRef({});

  // Memoized display date that updates when calendarDate changes
  const displayDate = useMemo(() => {
    if (calendarDate) {
      // Parse the calendar date from WebSocket (format: 'YYYY-MM-DD' or 'YYYY-MM-DDTHH:mm:ss...')
      const dateString = calendarDate.split('T')[0]; // Get just the date part
      const [year, month, day] = dateString.split('-');
      const parsedDate = new Date(Number(year), Number(month) - 1, Number(day));
      return parsedDate;
    }
    return new Date(); // Fallback to current date
  }, [calendarDate]);

  // Get all available channels from config - filter by visibility and sort alphabetically
  const availableChannels = Object.keys(channelConfig)
    .filter(key => 
      channelConfig[key] && 
      channelConfig[key].title && 
      channelConfig[key].visible !== false && // Show if visible is true or undefined (default visible)
      key !== 'employee' && // Exclude employee channel from home screen
      key !== 'live' // Exclude live channel from home screen - it's now an overlay, not a tile
    )
    .sort((a, b) => {
      // Sort alphabetically by title
      const titleA = (channelConfig[a].title || '').toLowerCase();
      const titleB = (channelConfig[b].title || '').toLowerCase();
      return titleA.localeCompare(titleB);
    });

  const handleAppClick = (channelKey, event) => {
    // If clicking the call icon and a call is already in progress, just navigate without animation
    if (channelKey === 'call' && isCallInProgress) {
      console.log('📞 Call already in progress, navigating to existing call');
      dispatch(setChannel('call'));
      return;
    }
    
    const tileElement = tileRefs.current[channelKey];
    if (tileElement && onAppOpen) {
      const rect = tileElement.getBoundingClientRect();
      const config = channelConfig[channelKey];
      const color = getChannelColor(channelKey);
      
      // Add pre-animation micro-interaction
      tileElement.classList.add('icon-tap-prep');
      
      // Trigger animation with tile info after micro-delay
      setTimeout(() => {
        tileElement.classList.remove('icon-tap-prep');
        onAppOpen({
          channelKey,
          rect,
          color,
          title: config.title,
          icon: getChannelIcon(channelKey),
          customIconUrl: getChannelCustomIconUrl(channelKey)
        });
      }, 100); // Match the pre-animation duration
    } else {
      // Fallback without animation
      // Mark as manual navigation for app channel to clear navigation params
      if (channelKey === 'app') {
        dispatch(setChannel({ channel: channelKey, manual: true }));
      } else {
        dispatch(setChannel(channelKey));
      }
    }
  };

  // Get icon and color for each channel
  const getChannelIcon = (channelKey) => {
    const config = channelConfig[channelKey];
    if (config.icon) return config.icon;
    
    // Default iOS-style icons for each channel type
    const defaultIcons = {
      sms: 'sms',
      email: 'mail',
      calendar: 'event',
      browser: 'public',
      app: 'account_balance',
      call: 'phone',
      agent: 'chat'
    };
    return defaultIcons[channelKey] || 'circle';
  };

  const getChannelCustomIconUrl = (channelKey) => {
    const config = channelConfig[channelKey];
    return config.customIconUrl || null;
  };

  const getChannelColor = (channelKey) => {
    const config = channelConfig[channelKey];
    if (config.color) return config.color;
    
    // Default iOS app colors
    const defaultColors = {
      sms: '#34C759', // Messages - Green
      email: '#007AFF', // Mail - Blue  
      calendar: '#FF3B30', // Calendar - Red
      browser: '#4285F4', // Chrome - Blue
      app: '#0066CC', // Banking - Bank Blue
      call: '#34C759', // Phone - Green
      agent: '#FF6B35' // AI Assistant - Orange
    };
    return defaultColors[channelKey] || '#007AFF';
  };

  const backgroundStyle = homeBackgroundImage 
    ? {
        backgroundImage: `url(${homeBackgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat',
        minHeight: '100vh'
      }
    : {};

  // Format the display date (without year)
  const formattedDate = displayDate.toLocaleDateString(undefined, { 
    weekday: 'long',
    month: 'long', 
    day: 'numeric'
  });

  return (
    <div className="home-view w-full flex flex-col relative overflow-hidden" 
         style={{
           ...backgroundStyle,
           height: '100%',
           minHeight: 0
         }}>
      {/* Add keyframe animations */}
      <style>
        {`
          @keyframes badgePop {
            0% {
              transform: scale(0);
              opacity: 0;
            }
            50% {
              transform: scale(1.2);
            }
            100% {
              transform: scale(1);
              opacity: 1;
            }
          }
        `}
      </style>
      
      {/* Background overlay for readability */}
      {homeBackgroundImage && (
        <div className="absolute inset-0 bg-black bg-opacity-30"></div>
      )}
      
      {/* Content container */}
      <div className="relative z-10 flex flex-col justify-center px-6 py-8" style={{ height: '100%' }}>
        {/* Status area */}
        <div className="text-center mb-8">
          <h1 className={`text-3xl mb-2 ${homeBackgroundImage ? 'text-white drop-shadow-lg' : 'text-gray-800'}`}
              style={{ 
                fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
                fontWeight: '300',
                letterSpacing: '-0.02em'
              }}>
            {formattedDate}
          </h1>
        </div>

        {/* Agent Widget - Always visible on home screen */}
        {channelConfig.agent?.widgetVisible !== false && (
          <div className="mb-8 flex justify-center">
            <div className="w-full max-w-sm">
              <AgentView 
                onNavigateToAgent={() => dispatch(setChannel('agent'))}
              />
            </div>
          </div>
        )}

        {/* App grid - Responsive grid with safe padding */}
        <div className="flex-1 flex items-center justify-center px-4 pb-24" style={{ maxHeight: '400px' }}>
          <div className="home-screen-tiles grid grid-cols-4 gap-4 sm:gap-6 w-full" style={{ maxWidth: '360px' }}>
            {availableChannels.map((channelKey) => {
              const config = channelConfig[channelKey];
              const icon = getChannelIcon(channelKey);
              const customIconUrl = getChannelCustomIconUrl(channelKey);
              const color = getChannelColor(channelKey);
              const unreadCount = unreadCounts[channelKey] || 0;
              
              return (
                <div key={channelKey} style={{ position: 'relative' }}>
                  {/* Notification Badge - completely outside button layout */}
                  {unreadCount > 0 && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '2px',
                        right: '2px',
                        minWidth: '20px',
                        height: '20px',
                        borderRadius: '10px',
                        backgroundColor: '#FF3B30',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '11px',
                        fontWeight: '600',
                        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", system-ui, sans-serif',
                        padding: '0 6px',
                        boxShadow: '0 2px 8px rgba(255, 59, 48, 0.5), 0 0 0 2px white',
                        zIndex: 10,
                        animation: 'badgePop 0.3s ease-out',
                        pointerEvents: 'none'
                      }}
                    >
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </div>
                  )}
                  
                  <button
                    ref={(el) => { tileRefs.current[channelKey] = el; }}
                    onClick={(event) => handleAppClick(channelKey, event)}
                    data-channel={channelKey}
                    className={`flex flex-col items-center space-y-2 p-2 rounded-2xl transition-all active:scale-95 ${
                      homeBackgroundImage ? 'hover:bg-white hover:bg-opacity-10' : 'hover:bg-gray-100'
                    }`}
                    style={{ minWidth: 0, width: '100%' }}
                  >
                  {/* App icon */}
                  <div 
                    className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl shadow-xl overflow-hidden relative flex-shrink-0"
                    style={{ 
                      backgroundColor: color,
                      boxShadow: '0 8px 32px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.2)'
                    }}
                  >
                    {customIconUrl ? (
                      <img 
                        src={customIconUrl} 
                        alt={config.title}
                        style={{ 
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          objectPosition: 'center',
                          filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))',
                          padding: '6px', // Small padding to ensure image doesn't touch edges and looks balanced
                          boxSizing: 'border-box',
                          // Enhanced image quality
                          imageRendering: '-webkit-optimize-contrast',
                          WebkitImageRendering: '-webkit-optimize-contrast',
                          msInterpolationMode: 'bicubic',
                          // Force GPU acceleration for crisp rendering
                          transform: 'translate3d(0, 0, 0)',
                          WebkitTransform: 'translate3d(0, 0, 0)',
                          backfaceVisibility: 'hidden',
                          WebkitBackfaceVisibility: 'hidden'
                        }}
                        onError={(e) => {
                          // Fallback to material icon if custom icon fails to load
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div 
                      className="absolute inset-0 flex items-center justify-center"
                      style={{ 
                        display: customIconUrl ? 'none' : 'flex'
                      }}
                    >
                      <span 
                        className="material-icons text-white" 
                        style={{ 
                          fontSize: '28px', 
                          textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                        }}
                      >
                        {icon}
                      </span>
                    </div>
                  </div>
                  
                  {/* App name */}
                  {/* App name */}
                  <span className={`text-xs text-center leading-tight w-full ${
                    homeBackgroundImage ? 'text-white drop-shadow-md' : 'text-gray-800'
                  }`}
                        style={{ 
                          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", system-ui, sans-serif',
                          fontWeight: '400',
                          letterSpacing: '-0.01em'
                        }}>
                    {config.title}
                  </span>
                </button>
              </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
